import './bar-chart.scss';
import _ from 'lodash';
import { Tooltip } from '../tooltip/tooltip';
import { DetailedHTMLProps, HTMLAttributes } from 'react';
import classNames from 'classnames';
import { safeRound } from '../../util/util';

export type BarChartData = {
  label: string;
  value: number;
};

export const BarChart = ({
  data,
  valueFormatter = (v) => v.toString(),
  className,
  ...props
}: { data: BarChartData[]; valueFormatter?: (v: number) => string } & DetailedHTMLProps<
  HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>) => {
  const maxValue = _.max(data.map((d) => d.value)) || 1;
  const steps = 3;
  const yAxisLabelStep = yAxisSizer(maxValue, steps);
  const yAxisMax = yAxisLabelStep * steps;
  return (
    <div className={classNames('bar-chart w-full flex gap-2', className)} {...props}>
      <div>
        <div className="flex flex-column h-full items-center justify-between pb-[0.5rem]">
          {Array.from(Array(steps + 1).keys()).map((i) => (
            <div key={i} className="bar-y-label">
              {yAxisFormatter((3 - i) * yAxisLabelStep)}
            </div>
          ))}
        </div>
      </div>
      <div className="h-full grow flex flex-column gap-2 rounded-2xl">
        <div className="flex items-end relative z-10 grow" style={{ gap: `${(100 / data.length) * (1 / 3)}%` }}>
          <div className="absolute h-full w-full -z-10 flex flex-column">
            {Array.from(Array(steps).keys()).map((i) => (
              <div key={i} className="range-section grow"></div>
            ))}
          </div>
          {data.map((d, i) => {
            const tooltip = <div className="bar-tooltip">{valueFormatter(d.value)}</div>;
            return (
              <div
                key={i}
                className={`bar rounded`}
                style={{
                  height: `${(d.value * 100) / yAxisMax}%`,
                  width: `${(100.0 / data.length) * (2 / 3)}%`,
                }}
              >
                <Tooltip type="black" className="w-full h-full" tooltip={tooltip}></Tooltip>
              </div>
            );
          })}
        </div>
        <div className="flex items-center" style={{ gap: `${(100 / data.length) * (1 / 3)}%` }}>
          {data.map((d, i) => {
            return (
              <div
                key={i}
                className="bar-label text-center"
                style={{
                  width: `${(100.0 / data.length) * (2 / 3)}%`,
                }}
              >
                {d.label}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

function yAxisFormatter(v: number): string {
  if (v >= 1000000) {
    return `${safeRound(v / 1000000, 1)}M`;
  } else if (v >= 1000) {
    return `${safeRound(v / 1000)}K`;
  } else {
    return v.toString();
  }
}

function yAxisSizer(maxValue: number, steps: number): number {
  let exp = 0;
  let base = maxValue;
  while (base > 100) {
    base /= 10;
    exp++;
  }
  return _.ceil(base / steps) * Math.pow(10, exp);
}
